import { useCallback, useEffect, useMemo, useState } from "react";
import { useToasts } from "../../contexts/toasts";
import { Variant } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Alert, Button, Container, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faPen,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import {
  ConfigProvider,
  Empty,
  Form,
  GetProp,
  Select,
  Space,
  Table,
} from "antd";
import IconButton from "../../common/IconButton";
import { Option, PagedResults } from "../../types/generics";
import VariantForm from "../../Components/Form/VariantForm";
import AppSearch from "../../common/AppSearch";
import ListPageHeader from "../../common/ListPageHeader";
import ListPanel from "../../common/ListPanel";
import { debounce } from "lodash";

export default function VariantsList() {
  const { showToast } = useToasts();
  const [variants, setVariants] = useState<Variant[]>();
  const [error, setError] = useState(false);
  const [totalVariants, setTotalVariants] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    pageSize: number;
    currentPage: number;
    freeText: string;
    exerciseId?: number;
  }>({
    pageSize: 20,
    currentPage: 1,
    freeText: "",
  });
  const [addVariantModal, setAddVariantModal] = useState(false);
  const [deleteVariantModal, setDeleteVariantModal] = useState(false);
  const [variantForm] = Form.useForm();
  const [selectedVariant, setSelectedVariant] = useState<Variant>();
  const [exercises, setExercises] = useState<Option[]>();

  const initVariants = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);
    if (filters.freeText !== "") urlParams.append("freeText", filters.freeText);
    if (filters.exerciseId)
      urlParams.append("exerciseId", `${filters.exerciseId}`);
    Entities.getPaginatedVariants(urlParams).then(
      ({ data }) => {
        setVariants((data as PagedResults<Variant>).items);
        setTotalVariants((data as PagedResults<Variant>).totalItems);
      },
      (err) => {
        showToast(
          "variants-error",
          "Impossibile ottenere la lista delle varianti",
          "",
          "danger"
        );
        setVariants([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  const initExercises = useCallback(() => {
    Entities.getExercises().then(
      ({ data }) => {
        setExercises(
          data.list.map((ex) => ({
            label: ex.name,
            value: ex.id!,
          }))
        );
      },
      () => {
        showToast(
          "exercises-error",
          "Impossibile ottenere la lista degli esercizi",
          "",
          "danger"
        );
        setExercises([]);
      }
    );
  }, [showToast]);

  const reset = useCallback(() => {
    setError(false);
    setVariants(undefined);
    initVariants();
    initExercises();
  }, [initVariants, initExercises]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: page,
    }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((previous) => ({
      ...previous,
      pageSize: size,
      currentPage: 1,
    }));
  }, []);

  const handleEditVariantClick = useCallback(
    (variant?: Variant) => () => {
      setSelectedVariant(variant);
      setAddVariantModal(true);
    },
    []
  );

  const handleFilterChange = useCallback((text: string) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: 1,
      freeText: text,
    }));
  }, []);

  const handleFieldChange = useCallback(
    (field: string) => (evt: any) => {
      setFilters((previous) => ({
        ...previous,
        [field]: evt,
      }));
    },
    []
  );

  const debounceSearchChange = useMemo(
    () => debounce(handleFilterChange, 500),
    [handleFilterChange]
  );

  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    };
  }, [debounceSearchChange]);

  const renderToolbar = useCallback(() => {
    return (
      <ListPageHeader
        title="Varianti"
        count={totalVariants}
        actions={
          <button
            onClick={handleEditVariantClick()}
            className="button-primary"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">Aggiungi Variante</span>
          </button>
        }
      >
        <AppSearch
          placeholder="Cerca per nome..."
          onChange={debounceSearchChange}
        />
        <Select
          value={filters.exerciseId}
          placeholder="Cerca per esercizio..."
          allowClear
          onChange={handleFieldChange("exerciseId")}
        >
          {(exercises || []).map((exercise, index) => (
            <Select.Option
              key={index}
              value={exercise.value}
              label={exercise.label}
            >
              <span>{exercise.label}</span>
            </Select.Option>
          ))}
        </Select>
      </ListPageHeader>
    );
  }, [
    handleEditVariantClick,
    exercises,
    debounceSearchChange,
    filters,
    handleFieldChange,
    totalVariants,
  ]);

  const handleDeleteVariantClick = useCallback(
    (variant: Variant) => () => {
      setSelectedVariant(variant);
      setDeleteVariantModal(true);
    },
    []
  );

  const renderEmpty: GetProp<typeof ConfigProvider, "renderEmpty"> = () => {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Nessuna Variante"
      />
    );
  };

  const renderVariants = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    return (
      <Table
        size="middle"
        bordered
        scroll={{ y: "calc(100vh  280px)" }}
        dataSource={variants}
        rowKey="id"
        locale={{
          emptyText: renderEmpty(),
        }}
        pagination={{
          pageSize: filters.pageSize,
          current: filters.currentPage,
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange,
          total: totalVariants,
        }}
      >
        <Table.Column
          key="name"
          dataIndex="name"
          title="Nome Variante"
          className="text-center col-pattern"
        />
        <Table.Column
          key="exerciseRef"
          dataIndex="exerciseRef"
          title="Esercizio"
          className="text-center col-pattern"
          render={(_value, record: Variant) => (
            <span>{record.exercise?.name}</span>
          )}
        />
        <Table.Column
          key="actions"
          title="Azioni"
          className="text-center col-pattern"
          render={(_value, record: Variant) => (
            <Space size={1}>
              <IconButton
                tooltip="Modifica"
                onClick={handleEditVariantClick(record)}
              >
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
              <IconButton
                tooltip="Elimina"
                className="text-danger"
                onClick={handleDeleteVariantClick(record)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    );
  }, [
    variants,
    error,
    filters,
    handlePageChange,
    handlePageSizeChange,
    totalVariants,
    handleEditVariantClick,
    handleDeleteVariantClick,
  ]);

  const renderAsyncContent = useCallback(() => {
    return <ListPanel loading={!variants}>{renderVariants()}</ListPanel>;
  }, [variants, renderVariants]);

  const handleAddVariantModalClose = useCallback(() => {
    setAddVariantModal(false);
    variantForm.resetFields();
    setSelectedVariant(undefined);
  }, [variantForm]);

  const handleDeleteVariantModalClose = useCallback(() => {
    setDeleteVariantModal(false);
    setSelectedVariant(undefined);
  }, []);

  const handleEditVariantSubmit = useCallback(
    (values: any) => {
      setLoading(true);

      const body = { ...values };
      const promise = selectedVariant
        ? Entities.updateVariant(selectedVariant.id!, body)
        : Entities.createVariant(body);

      promise.then(
        () => {
          setLoading(false);
          showToast(
            "variant-success",
            `Variante ${
              selectedVariant ? "Modificata" : "Salvata"
            } correttamente`,
            undefined,
            "success"
          );
          handleAddVariantModalClose();
          reset();
        },
        () => {
          setLoading(false);
          showToast(
            "variant-error",
            `Errore nel${selectedVariant ? "la" : ""} ${
              selectedVariant ? "Modifica" : "Salvataggio"
            } della Variante`,
            undefined,
            "danger"
          );
        }
      );
    },
    [selectedVariant, showToast, handleAddVariantModalClose, reset]
  );

  const handleDeleteVariant = useCallback(() => {
    setLoading(true);

    Entities.deleteVariant(selectedVariant!.id!).then(
      () => {
        setLoading(false);
        handleDeleteVariantModalClose();
        reset();
        showToast(
          "delete-variant-success",
          "Variante correttamente eliminata",
          undefined,
          "success"
        );
      },
      () => {
        setLoading(false);
        showToast(
          "delete-variant-error",
          "Errore nella cancellazione della Variante, per favore riprova",
          undefined,
          "danger"
        );
      }
    );
  }, [selectedVariant, showToast, handleDeleteVariantModalClose, reset]);

  return (
    <Container fluid="md" className="mt-5 list-page-container">
      {renderToolbar()}
      {renderAsyncContent()}

      <Modal show={addVariantModal} onHide={handleAddVariantModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedVariant ? "Modifica" : "Nuova"} Variante
          </Modal.Title>
        </Modal.Header>
        <VariantForm
          variant={selectedVariant}
          options={exercises}
          onClose={handleAddVariantModalClose}
          onSubmit={handleEditVariantSubmit}
          loading={loading}
        />
      </Modal>
      <Modal show={deleteVariantModal} onHide={handleDeleteVariantModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedVariant ? "Modifica" : "Nuova"} Variante
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare la Variante{" "}
            <strong>{selectedVariant?.name}</strong>?
          </p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteVariantModalClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteVariant}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
